// Shared "Build" strategy model (LAB) — extracted from chart.ts so BOTH the chart
// window and the Build side panel can use one source of truth (chart.ts self-runs
// on import, so it can't be imported). Pure: no DOM/canvas state, no side effects.
// Contains: the why menu + human dials, per-bar evaluation, the trade simulator,
// level detection, and the compile-to-engine mapping.
import type { OHLCPoint } from "./market-data.js";
import { condL, blankStrategy, newSetup, group as lgroup, defaultManage, newManageRule, type Strategy, type LNode, type ManageRule } from "./composable/model.js";

// --- Chart-native "why" toolbar (LAB experiment, D-???) ---------------------
// The full "pick your why" menu, straight on the chart. Toggling a why marks
// every bar where it was true. GEOMETRIC whys (support / breakout) drop a
// DRAGGABLE dashed level — dragging it retunes the rule by eye. OCCURRENCE whys
// mark bars from a rule computed off the candles. PREVIEW whys are the ones that
// need a second instrument or a live data feed we don't have on the chart yet —
// they stay in the menu as placeholders so the family map isn't lost (per
// founder: leave placeholders rather than drop anything).
// Lives on the real chart canvas next to indicators. Not persisted/deployed yet.
export type WhyKind =
  | "ride_trend" | "momentum"
  | "fade_stretch" | "level"
  | "breakout" | "retest"
  | "sweep" | "fakeout"
  | "leader" | "diverge"
  | "gap" | "event"
  | "funding" | "turnmonth" | "session";

export type WhyShape = "geometric" | "occurrence" | "preview";
// A human-language dial: the slider runs 0→100 between two plain-language ends;
// `toParam` maps that human position to the actual technical value the rule uses.
// The trader never sees "RSI 42" — they see "Shallow dip ←→ Deep dip".
export interface WhyDial { key: string; left: string; right: string; def: number; toParam: (human: number) => number }
export interface WhyDef {
  id: WhyKind;
  group: string;
  label: string; // the question
  gloss: string;
  color: string;
  shape: WhyShape;
  note?: string; // why it's a preview (only for shape === "preview")
  dials?: WhyDial[]; // human-language tuning dials (0..100 each)
}
export type WhyRole = "trigger" | "filter";
export interface WhyState { def: WhyDef; price?: number; tol?: number; params?: Record<string, number>; role?: WhyRole }

/** Raw technical value for a why's dial, from its (human 0..100) stored position. */
export function dialRaw(id: WhyKind, key: string, human: number | undefined): number {
  const d = WHY_DEFS.find((x) => x.id === id)?.dials?.find((x) => x.key === key);
  return d ? d.toParam(human ?? d.def) : 0;
}
export function dv(w: WhyState, key: string): number { return dialRaw(w.def.id, key, w.params?.[key]); }

/** One short word per trade type — used everywhere on the chart (stats bar, tune
 * strip, strategy menu) so the long `label` sentences never appear on the chart. */
export const WHY_WORD: Record<WhyKind, string> = {
  ride_trend: "Pullback", momentum: "Momentum",
  fade_stretch: "Fade", level: "Level",
  breakout: "Breakout", retest: "Retest",
  sweep: "Sweep", fakeout: "Fakeout",
  leader: "Leader", diverge: "Divergence",
  gap: "Gap", event: "Event",
  funding: "Funding", turnmonth: "Calendar", session: "Session",
};

export const PREVIEW_GREY = "#8a94a6";
export const MANAGE_STOP_COLOR = "#6b7280"; // slate — the invalidation level
export const MANAGE_TARGET_COLOR = "#2f6df6"; // blue — the objective level
export const WHY_DEFS: WhyDef[] = [
  // Trend / momentum
  { id: "ride_trend", group: "Trend / momentum", label: "Ride a trend and buy the pullbacks", gloss: "buy a dip inside an uptrend", color: "#2f6df6", shape: "occurrence",
    dials: [
      { key: "depth", left: "Shallow dip", right: "Deep dip", def: 55, toParam: (h) => 48 - h * 0.13 }, // RSI 48→35 (dips this shallow/deep actually occur)
      { key: "trend", left: "Any uptrend", right: "Strong uptrend", def: 30, toParam: (h) => h / 100 * 0.18 }, // 50-avg slope %/5 bars 0→0.18 (achievable)
      { key: "confirm", left: "Buy the dip", right: "Wait for the bounce", def: 0, toParam: (h) => h / 100 }, // ≥0.5 → need an up close (really on/off)
    ] },
  { id: "momentum", group: "Trend / momentum", label: "Get in as momentum ignites", gloss: "a fresh trend / thrust", color: "#4b8bf5", shape: "occurrence",
    dials: [
      { key: "push", left: "Any push", right: "Strong push", def: 30, toParam: (h) => 0.3 + h / 100 * 1.7 }, // thrust % 0.3→2.0
      { key: "trend", left: "Any uptrend", right: "Strong uptrend", def: 20, toParam: (h) => h / 100 * 1.0 }, // 20-vs-50 gap % 0→1.0
    ] },
  // Mean reversion
  { id: "fade_stretch", group: "Mean reversion", label: "Fade price when it's stretched too far", gloss: "overextended from normal", color: "#e79320", shape: "occurrence",
    dials: [
      { key: "stretch", left: "A little stretched", right: "Very stretched", def: 80, toParam: (h) => 0.25 - h / 100 * 0.23 }, // %B 0.25→0.02
      { key: "confirm", left: "Fade the stretch", right: "Wait for the turn", def: 0, toParam: (h) => h / 100 }, // ≥0.5 → need an up close
    ] },
  { id: "level", group: "Mean reversion", label: "Buy at support / sell at resistance", gloss: "revert to a level", color: "#17a2b8", shape: "geometric",
    dials: [
      { key: "tol", left: "Exact level", right: "Loose touch", def: 40, toParam: (h) => 0.1 + h / 100 * 1.4 }, // % distance 0.1→1.5
      { key: "confirm", left: "Any touch", right: "Reversal close", def: 60, toParam: (h) => h / 100 }, // ≥0.5 → need an up close off the line
    ] },
  // Breakout / volatility
  { id: "breakout", group: "Breakout / volatility", label: "Trade a break out of a range", gloss: "price leaves the range", color: "#8e44ad", shape: "geometric",
    dials: [
      { key: "move", left: "Small move", right: "Big move", def: 20, toParam: (h) => h / 100 * 1.0 }, // must close this % beyond
      { key: "confirm", left: "Fast confirmation", right: "Strong confirmation", def: 0, toParam: (h) => 1 + Math.round(h / 100 * 2) }, // bars held: 1→3
    ] },
  { id: "retest", group: "Breakout / volatility", label: "Trade the retest after a breakout holds", gloss: "break → retest → go", color: PREVIEW_GREY, shape: "preview",
    note: "Needs the breakout level first — this one keys off it. Coming next." },
  // Liquidity / structure
  { id: "sweep", group: "Liquidity / structure", label: "Trade a stop-run — grab a low then snap back", gloss: "sweep & reclaim", color: "#159a8c", shape: "occurrence",
    dials: [
      { key: "width", left: "Recent low", right: "Wider low", def: 35, toParam: (h) => Math.round(5 + h / 100 * 15) }, // lookback 5→20
      { key: "reclaim", left: "Any reclaim", right: "Strong reclaim", def: 0, toParam: (h) => h / 100 * 0.5 }, // close this % back above the low
    ] },
  { id: "fakeout", group: "Liquidity / structure", label: "Trade a failed breakout back into the range", gloss: "the fakeout", color: "#16a085", shape: "occurrence",
    dials: [
      { key: "width", left: "Recent high", right: "Wider high", def: 35, toParam: (h) => Math.round(5 + h / 100 * 15) }, // lookback 5→20
      { key: "reject", left: "Any rejection", right: "Strong rejection", def: 0, toParam: (h) => h / 100 * 0.5 }, // close this % back below the high
    ] },
  // Relative / intermarket
  { id: "leader", group: "Relative / intermarket", label: "When one market leads, trade the one that follows", gloss: "leader / laggard", color: PREVIEW_GREY, shape: "preview",
    note: "Needs a second instrument to compare against. Coming when the chart can hold two." },
  { id: "diverge", group: "Relative / intermarket", label: "Trade two related markets pulling apart", gloss: "divergence / pairs", color: PREVIEW_GREY, shape: "preview",
    note: "Needs a second instrument to compare against. Coming when the chart can hold two." },
  // Stocks
  { id: "gap", group: "Stocks", label: "Trade the gap at the open", gloss: "gap-fill / gap-and-go", color: "#d6336c", shape: "occurrence",
    dials: [{ key: "size", left: "Small gap", right: "Big gap", def: 30, toParam: (h) => 0.5 + h / 100 * 3.5 }] }, // gap % 0.5→4
  // Event / catalyst
  { id: "event", group: "Event / catalyst", label: "Trade the reaction to a scheduled event", gloss: "news / data / earnings", color: PREVIEW_GREY, shape: "preview",
    note: "Needs the news/earnings calendar wired to the chart. Coming next." },
  // Crypto
  { id: "funding", group: "Crypto", label: "Trade funding-rate extremes", gloss: "crowd positioning", color: PREVIEW_GREY, shape: "preview",
    note: "Needs a live perp funding feed. Coming when that data lands." },
  // Session / time
  { id: "turnmonth", group: "Session / time", label: "Trade a recurring calendar effect", gloss: "turn of the month", color: "#b07d2b", shape: "occurrence",
    dials: [{ key: "window", left: "First bar only", right: "First few bars", def: 0, toParam: (h) => 1 + Math.round(h / 100 * 4) }] }, // 1→5 bars into the month
  { id: "session", group: "Session / time", label: "Trade a specific session or time of day", gloss: "e.g. the NY open", color: PREVIEW_GREY, shape: "preview",
    note: "Needs an intraday timeframe to mean anything. Switch to 5m/30m — coming next." },
];

// Small line glyphs (16×16 viewBox) — one per why, reusing the watch-settings
// trade-type icons where they match (dip, stretched, breakout, trend). Rendered
// in the why's colour on the strategy cards.
export const WHY_ICON: Record<WhyKind, string> = {
  ride_trend: "M3 6l4 5 6-8",                                             // dip (reused)
  momentum: "M2 12l4-4 3 2 5-7",                                          // trend (reused)
  fade_stretch: "M3 8h10 M5.5 5.5L3 8l2.5 2.5 M10.5 5.5L13 8l-2.5 2.5",   // stretched (reused)
  level: "M2 10h12 M6 10l2-2 2 2",                                        // bounce off a level
  breakout: "M8 13V4 M5 7l3-3 3 3 M3 11h10",                              // breakout (reused)
  retest: "M8 13V6 M5 9l3-3 3 3 M3 12h4 M9 12h4",                         // break + retest
  sweep: "M3 4v4a5 5 0 0 0 10 0V4",                                       // grab low, reclaim (cup)
  fakeout: "M2 12l6-8 2 4 M10 8l4 4 M3 12h10",                            // spike that fails back
  leader: "M4 12V7 M8 12V4 M12 12V9",                                     // leader (reused)
  diverge: "M2 5l12 6 M2 11l12-6",                                        // divergence (reused)
  gap: "M4 12V8 M12 8V4 M7 9h2",                                          // a gap between two bars
  event: "M8 2L5 9h4l-2 5",                                               // catalyst (bolt)
  funding: "M8 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4",                            // dot (placeholder)
  turnmonth: "M3 4h10v9H3z M3 7h10 M6 3v2 M10 3v2",                       // calendar
  session: "M8 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10 M8 5.5V8l2 1.5",           // clock
};

export const whyPriorLow = (b: OHLCPoint[], i: number, n: number): number => {
  let m = Infinity;
  for (let k = Math.max(0, i - n); k < i; k++) m = Math.min(m, b[k]!.low);
  return m;
};
export const whyPriorHigh = (b: OHLCPoint[], i: number, n: number): number => {
  let m = -Infinity;
  for (let k = Math.max(0, i - n); k < i; k++) m = Math.max(m, b[k]!.high);
  return m;
};
/** Index-aligned simple moving average of close (null until enough bars). */
export function whySMA(b: OHLCPoint[], p: number): Array<number | null> {
  const out = new Array<number | null>(b.length).fill(null);
  let sum = 0;
  for (let i = 0; i < b.length; i++) {
    sum += b[i]!.close;
    if (i >= p) sum -= b[i - p]!.close;
    if (i >= p - 1) out[i] = sum / p;
  }
  return out;
}
/** Index-aligned Wilder RSI of close. */
export function whyRSI(b: OHLCPoint[], p = 14): Array<number | null> {
  const out = new Array<number | null>(b.length).fill(null);
  let g = 0, l = 0;
  for (let i = 1; i < b.length; i++) {
    const d = b[i]!.close - b[i - 1]!.close, up = Math.max(0, d), dn = Math.max(0, -d);
    if (i <= p) { g += up; l += dn; if (i === p) { g /= p; l /= p; out[i] = 100 - 100 / (1 + (l === 0 ? 100 : g / l)); } }
    else { g = (g * (p - 1) + up) / p; l = (l * (p - 1) + dn) / p; out[i] = 100 - 100 / (1 + (l === 0 ? 100 : g / l)); }
  }
  return out;
}
/** %B (position inside Bollinger Bands, 0=lower 1=upper) over close, length p. */
export function whyPctB(b: OHLCPoint[], p = 20): number[] {
  const c = b.map((x) => x.close);
  const out = new Array<number>(c.length).fill(0.5);
  for (let i = p - 1; i < c.length; i++) {
    let mean = 0;
    for (let k = i - p + 1; k <= i; k++) mean += c[k]!;
    mean /= p;
    let v = 0;
    for (let k = i - p + 1; k <= i; k++) v += (c[k]! - mean) ** 2;
    const sd = Math.sqrt(v / p), up = mean + 2 * sd, lo = mean - 2 * sd;
    out[i] = up === lo ? 0.5 : (c[i]! - lo) / (up - lo);
  }
  return out;
}
/** Precomputed series shared across whys for one render (built on demand). */
export interface WhyCtx { pb?: number[]; sma20?: Array<number | null>; sma50?: Array<number | null>; rsi?: Array<number | null>; }
/** Whys that describe a market REGIME (a background condition) rather than a
 * single event. We shade the regime as a ribbon and only mark entries inside it. */
export const WHY_HAS_REGIME = new Set<WhyKind>(["ride_trend", "momentum"]);
/** Is bar i inside this why's regime (the trend it wants to trade within)? Mirrors
 * for a short strategy (a genuine DOWNtrend) when `long` is false. */
export function whyRegime(b: OHLCPoint[], i: number, w: WhyState, ctx: WhyCtx, long = true): boolean {
  if (i < 25) return false;
  switch (w.def.id) {
    case "ride_trend": {
      // A real trend: price the right side of a 50-average that's sloping the right
      // way, at least as fast as the "trend" dial asks (0 → any slope; higher → steeper).
      const s = ctx.sma50![i], s5 = ctx.sma50![i - 5];
      if (s == null || s5 == null) return false;
      const slope = s5 > 0 ? (s - s5) / s5 * 100 : 0;
      return long
        ? b[i]!.close > s && slope > 0 && slope >= dv(w, "trend")
        : b[i]!.close < s && slope < 0 && -slope >= dv(w, "trend");
    }
    case "momentum": {
      // 20-avg the right side of the 50-avg, separated by at least the "trend" gap.
      const a = ctx.sma20![i], c = ctx.sma50![i];
      if (a == null || c == null) return false;
      return long
        ? a > c && (a - c) / c * 100 >= dv(w, "trend")
        : a < c && (c - a) / c * 100 >= dv(w, "trend");
    }
    default:
      return false;
  }
}
/** Did why `w` fire on bar i? Reads precomputed series from ctx. Mirrors for a
 * short strategy when `long` is false (sell the rally, fade the low stretch, etc.). */
export function whyFires(b: OHLCPoint[], i: number, w: WhyState, ctx: WhyCtx, long = true): boolean {
  if (i < 25) return false;
  const dirClose = long ? b[i]!.close > b[i]!.open : b[i]!.close < b[i]!.open; // a bar closing our way
  switch (w.def.id) {
    case "ride_trend": {
      // Buy the pullback (or sell the rally): inside the trend, when RSI has pulled
      // back. "confirm" ≥ 0.5 also waits for the turn (a bar closing our way).
      const r = ctx.rsi![i];
      if (!whyRegime(b, i, w, ctx, long) || r == null) return false;
      const dipped = long ? r < dv(w, "depth") : r > 100 - dv(w, "depth");
      if (!dipped) return false;
      return dv(w, "confirm") < 0.5 || dirClose;
    }
    case "momentum": {
      const o = b[i]!.open;
      const thrust = long ? (b[i]!.close - o) / o * 100 : (o - b[i]!.close) / o * 100;
      return whyRegime(b, i, w, ctx, long) && dirClose && thrust > dv(w, "push");
    }
    case "fade_stretch": {
      // Long fades a low stretch (near the lower band); short fades a high stretch.
      const pb = ctx.pb![i] ?? 0.5;
      if (long ? pb > dv(w, "stretch") : pb < 1 - dv(w, "stretch")) return false;
      return dv(w, "confirm") < 0.5 || dirClose; // optional turn
    }
    case "level": {
      if (w.price == null) return false;
      const dist = long ? Math.abs(b[i]!.low - w.price) : Math.abs(b[i]!.high - w.price);
      if (dist / w.price * 100 > dv(w, "tol")) return false;
      return dv(w, "confirm") < 0.5 || dirClose; // touch, or a reversal close off the line
    }
    case "breakout": {
      if (w.price == null) return false;
      const need = Math.max(1, Math.round(dv(w, "confirm")));
      if (i - need < 0) return false;
      if (long) {
        const adj = w.price * (1 + dv(w, "move") / 100);
        if (b[i]!.close <= adj) return false;
        for (let k = i - need + 1; k <= i; k++) if (b[k]!.close <= w.price) return false; // held above
        return b[i - need]!.close <= w.price; // fresh break `need` bars ago
      }
      const adj = w.price * (1 - dv(w, "move") / 100);
      if (b[i]!.close >= adj) return false;
      for (let k = i - need + 1; k <= i; k++) if (b[k]!.close >= w.price) return false; // held below
      return b[i - need]!.close >= w.price;
    }
    case "sweep": {
      // Long grabs a low and reclaims; short grabs a high and rejects.
      if (long) {
        const lo = whyPriorLow(b, i, Math.round(dv(w, "width")));
        return b[i]!.low < lo && b[i]!.close > lo * (1 + dv(w, "reclaim") / 100);
      }
      const hi = whyPriorHigh(b, i, Math.round(dv(w, "width")));
      return b[i]!.high > hi && b[i]!.close < hi * (1 - dv(w, "reclaim") / 100);
    }
    case "fakeout": {
      // Long = a failed break DOWN (poke a low, close back above); short = the mirror.
      if (long) {
        const lo = whyPriorLow(b, i, Math.round(dv(w, "width")));
        return b[i]!.low < lo && b[i]!.close > lo * (1 + dv(w, "reject") / 100);
      }
      const hi = whyPriorHigh(b, i, Math.round(dv(w, "width")));
      return b[i]!.high > hi && b[i]!.close < hi * (1 - dv(w, "reject") / 100);
    }
    case "gap":
      return i > 0 && Math.abs((b[i]!.open - b[i - 1]!.close) / b[i - 1]!.close * 100) > dv(w, "size");
    case "turnmonth": {
      // Within the first N bars of a new month (N from the "window" dial).
      const win = Math.max(1, Math.round(dv(w, "window")));
      for (let k = i; k > i - win && k > 0; k--) if (b[k]!.date.getMonth() !== b[k - 1]!.date.getMonth()) return true;
      return false;
    }
    default:
      return false; // preview whys never fire
  }
}
/** A FILTER is "satisfied" at bar i if its condition HOLDS there — for regime whys
 * that means the regime is in force; for event whys it means the event fires. */
export function whyFilterSatisfied(b: OHLCPoint[], i: number, w: WhyState, ctx: WhyCtx, long = true): boolean {
  return WHY_HAS_REGIME.has(w.def.id) ? whyRegime(b, i, w, ctx, long) : whyFires(b, i, w, ctx, long);
}
/** Entry bars for a stack of whys, combined as confluence: a TRIGGER fires AND
 * every FILTER holds at that bar. (No explicit trigger → every why acts as one.)
 * `long` mirrors every why's detection for a short strategy. */
export function computeEntries(b: OHLCPoint[], whys: WhyState[], ctx: WhyCtx, long = true): number[] {
  if (!whys.length) return [];
  const triggers = whys.filter((w) => (w.role ?? "trigger") === "trigger");
  const filters = whys.filter((w) => (w.role ?? "trigger") === "filter");
  const trig = triggers.length ? triggers : whys; // all-filters fallback: treat as triggers
  const out: number[] = [];
  for (let i = 0; i < b.length; i++) {
    if (!trig.some((w) => whyFires(b, i, w, ctx, long))) continue;
    if (filters.every((w) => whyFilterSatisfied(b, i, w, ctx, long))) out.push(i);
  }
  return out;
}
export const whySeedSupport = (b: OHLCPoint[]): number => {
  if (!b.length) return 0;
  const n = b.slice(-60);
  return Math.min(...n.map((c) => c.low));
};
export const whySeedBreakout = (b: OHLCPoint[]): number => {
  if (!b.length) return 0;
  const n = b.slice(Math.max(0, b.length - 90), Math.max(1, b.length - 20));
  return n.length ? Math.max(...n.map((c) => c.high)) : Math.max(...b.map((c) => c.high));
};
/** Small filled up-triangle centred at (x,y). */
export function whyTriangle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x, y - r);
  ctx.lineTo(x - r, y + r);
  ctx.lineTo(x + r, y + r);
  ctx.closePath();
  ctx.fill();
}
/** Plain-English statement of a why's current rule (reflects its tuned dials). */
export function whyRuleText(w: WhyState): string {
  switch (w.def.id) {
    case "ride_trend": return `In an uptrend, buy when the pullback dips to RSI ${dv(w, "depth").toFixed(0)}${dv(w, "confirm") >= 0.5 ? " and the bar closes up" : ""}.`;
    case "momentum": return `Uptrend with an up-bar bigger than ${dv(w, "push").toFixed(1)}%.`;
    case "fade_stretch": return `Price stretched to the low Bollinger band (%B ≤ ${dv(w, "stretch").toFixed(2)})${dv(w, "confirm") >= 0.5 ? ", closing up" : ""}.`;
    case "level": return `Comes within ${dv(w, "tol").toFixed(1)}% of your line${w.price != null ? ` (${w.price.toFixed(1)})` : ""}${dv(w, "confirm") >= 0.5 ? " and closes up" : ""}.`;
    case "breakout": return `Closes ${dv(w, "move") > 0.01 ? `${dv(w, "move").toFixed(2)}% ` : ""}past your line${w.price != null ? ` (${w.price.toFixed(1)})` : ""}${dv(w, "confirm") > 1 ? `, held ${Math.round(dv(w, "confirm"))} bars` : ""}.`;
    case "sweep": return `Dips below the ${Math.round(dv(w, "width"))}-bar low, then closes ${dv(w, "reclaim") > 0.01 ? `${dv(w, "reclaim").toFixed(2)}% ` : ""}back above it.`;
    case "fakeout": return `Pokes above the ${Math.round(dv(w, "width"))}-bar high, then closes ${dv(w, "reject") > 0.01 ? `${dv(w, "reject").toFixed(2)}% ` : ""}back below it.`;
    case "gap": return `Opens more than ${dv(w, "size").toFixed(1)}% away from the prior close.`;
    case "turnmonth": return dv(w, "window") > 1 ? `Within the first ${Math.round(dv(w, "window"))} bars of a new month.` : `The first trading bar of a new month.`;
    default: return w.def.gloss;
  }
}

// --- Manage / trade simulation (LAB) ----------------------------------------
// A why gives ENTRIES; management turns them into TRADES. Aaron's logic, level-
// based: exit on a CLOSE back through the stop (the invalidation level), take the
// target when it's touched ("take the TP when it's hit"). One position at a time.
export type TradeDir = "long" | "short";
export type ManageMode = "levels" | "structure";
// "levels": one absolute stop + target drawn on the chart (level-based setups).
// "structure": per-trade — stop at each entry's own recent swing, target = R multiple
// of that risk (so trend/occurrence whys produce a real sample of trades).
export interface ManageState { dir: TradeDir; mode: ManageMode; stop?: number; target?: number; swingLB?: number; rMult?: number }
export interface SimTrade { entryIdx: number; exitIdx: number; entry: number; exit: number; r: number; win: boolean }
export interface TradeStats { trades: number; winPct: number; expectancyR: number; avgWinR: number; avgLossR: number; maxDDr: number }

/** Simulate one-at-a-time trades. Stop is close-based (exit on a close back through
 * it), target is touch-based ("take the TP when it's hit"). R is measured against the
 * entry→stop distance. Level mode uses the drawn stop/target; structure mode derives
 * a per-trade swing stop and an R-multiple target. */
export function simulateTrades(b: OHLCPoint[], entries: number[], m: ManageState): SimTrade[] {
  const long = m.dir === "long";
  const levels = m.mode !== "structure";
  if (levels && (m.stop == null || m.target == null)) return [];
  const lb = m.swingLB ?? 10, rM = m.rMult ?? 2;
  const entrySet = new Set(entries);
  const trades: SimTrade[] = [];
  let inTrade = false, entryIdx = 0, entryPrice = 0, curStop = 0, curTarget = 0;
  for (let k = 0; k < b.length; k++) {
    if (!inTrade) {
      if (!entrySet.has(k)) continue;
      const px = b[k]!.close;
      let st: number, tg: number;
      if (levels) {
        st = m.stop!; tg = m.target!;
        const ok = long ? st < px && tg > px : st > px && tg < px;
        if (!ok) continue; // this entry doesn't sit between the drawn levels
      } else {
        if (k < lb) continue;
        st = long ? Math.min(whyPriorLow(b, k, lb), b[k]!.low) : Math.max(whyPriorHigh(b, k, lb), b[k]!.high);
        const risk = long ? px - st : st - px;
        if (risk <= 0) continue;
        tg = long ? px + rM * risk : px - rM * risk;
      }
      inTrade = true; entryIdx = k; entryPrice = px; curStop = st; curTarget = tg;
      continue;
    }
    const bar = b[k]!;
    const denom = long ? entryPrice - curStop : curStop - entryPrice;
    if (denom <= 0) { inTrade = false; continue; }
    const hitTarget = long ? bar.high >= curTarget : bar.low <= curTarget;
    const closeStop = long ? bar.close <= curStop : bar.close >= curStop;
    if (hitTarget) {
      const r = long ? (curTarget - entryPrice) / denom : (entryPrice - curTarget) / denom;
      trades.push({ entryIdx, exitIdx: k, entry: entryPrice, exit: curTarget, r, win: true });
      inTrade = false;
    } else if (closeStop) {
      const exit = bar.close;
      const r = long ? (exit - entryPrice) / denom : (entryPrice - exit) / denom;
      trades.push({ entryIdx, exitIdx: k, entry: entryPrice, exit, r, win: false });
      inTrade = false;
    }
  }
  return trades;
}

/** The whole chart-authored strategy, serialized for persistence + (later) deploy.
 * It captures the entry whys (+ tuned params), the management, and risk size —
 * everything Build produces. Keyed per symbol+timeframe (LAB). */
export interface SavedStudio {
  name: string;
  whys: Array<{ id: WhyKind; price?: number; tol?: number; params?: Record<string, number>; role?: WhyRole }>;
  manage: ManageState;
  riskPct: number;
}

// --- Compile a Build strategy into the composable engine (LAB, D-???) --------
// Maps each entry why to the nearest block(s) in the existing REGISTRY, wraps them
// in an AND group, approximates the level-based manage as a %/R stop+target (the
// block engine's manage vocabulary), and sets risk sizing. The result is a real
// composable `Strategy` that Advanced (Tune) opens and Deploy can run. Whys with
// no clean block equivalent (gap, turn-of-month) are reported as skipped.
export function whyToBlocks(id: WhyKind, params: Record<string, number> | undefined, long: boolean): LNode[] | null {
  const raw = (key: string) => dialRaw(id, key, params?.[key]);
  switch (id) {
    case "ride_trend": return [condL("priceAboveSma", 50, 0, 0), condL("rsiBelow", 14, Math.round(raw("depth")), 0)];
    case "momentum": return [condL("priceAboveSma", 50, 0, 0), condL(long ? "bullCandle" : "bearCandle", 0, 0, 0)];
    case "fade_stretch": return [condL(long ? "belowLowerBand" : "aboveUpperBand", 20, 0, 0)];
    case "level": return [condL("atSupport", 0, 0, 0)];
    case "breakout": return [condL(long ? "breakoutHigh" : "breakoutLow", 20, 0, 0)];
    case "sweep": return [condL(long ? "sweepReclaimLong" : "sweepReclaimShort", Math.round(raw("width")), 0, 0)];
    case "fakeout": return [condL(long ? "breakRetestShort" : "breakRetestLong", Math.round(raw("width")), 0, 0)];
    default: return null; // gap, turnmonth, previews — no clean block yet
  }
}
export function compileStudio(saved: SavedStudio, sym: string): { strategy: Strategy; skipped: WhyKind[] } {
  const dir: "long" | "short" = saved.manage?.dir ?? "long";
  const long = dir === "long";
  const conds: LNode[] = [];
  const skipped: WhyKind[] = [];
  for (const w of saved.whys ?? []) {
    const blocks = whyToBlocks(w.id, w.params, long);
    if (!blocks) { skipped.push(w.id); continue; }
    conds.push(...blocks);
  }
  const setup = newSetup();
  setup.name = "Entry";
  setup.direction = dir;
  setup.entry = lgroup("and", conds);
  setup.exit = lgroup("and", []);
  // Manage: structural stop (approx 1.5% loss → close all) + an R-multiple target.
  const rMult = saved.manage?.rMult ?? 2;
  const target = newManageRule();
  target.when = { kind: "profit", value: Number((1.5 * rMult).toFixed(1)) };
  target.then = { kind: "closeAll" };
  setup.manage = [...defaultManage(), target] as ManageRule[];
  const strat = blankStrategy();
  strat.name = saved.name?.trim() || `${sym} (Build)`;
  strat.setups = [setup];
  strat.sizing = { mode: "risk", size: strat.sizing.size, riskPct: saved.riskPct ?? 1 };
  return { strategy: strat, skipped };
}

export function statsFromTrades(trades: SimTrade[]): TradeStats {
  const n = trades.length;
  if (!n) return { trades: 0, winPct: 0, expectancyR: 0, avgWinR: 0, avgLossR: 0, maxDDr: 0 };
  const wins = trades.filter((t) => t.win);
  const losses = trades.filter((t) => !t.win);
  const mean = (a: number[]) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
  // max drawdown of the cumulative-R equity curve.
  let peak = 0, cum = 0, maxDD = 0;
  for (const t of trades) { cum += t.r; peak = Math.max(peak, cum); maxDD = Math.min(maxDD, cum - peak); }
  return {
    trades: n,
    winPct: (wins.length / n) * 100,
    expectancyR: mean(trades.map((t) => t.r)),
    avgWinR: mean(wins.map((t) => t.r)),
    avgLossR: mean(losses.map((t) => t.r)),
    maxDDr: maxDD,
  };
}

// --- Auto-detected liquidity levels (LAB) -----------------------------------
// Aaron's levels are double tops/bottoms & equal highs/lows, plus round numbers.
// We find swing pivots, cluster the ones that repeat at a price (that repetition
// IS a double-top / equal-high), and add psychological round numbers. The app
// proposes these so the trader confirms a level instead of hand-drawing it.
export interface DetectedLevel { price: number; kind: "high" | "low" | "round"; touches: number }
export function detectLevels(b: OHLCPoint[]): DetectedLevel[] {
  const n = b.length;
  if (n < 20) return [];
  const k = 3; // pivot half-window: a bar that's the local extreme of ±3 bars
  const pivHigh: number[] = [], pivLow: number[] = [];
  for (let i = k; i < n - k; i++) {
    let hi = true, lo = true;
    for (let j = i - k; j <= i + k; j++) {
      if (j === i) continue;
      if (b[j]!.high >= b[i]!.high) hi = false;
      if (b[j]!.low <= b[i]!.low) lo = false;
    }
    if (hi) pivHigh.push(b[i]!.high);
    if (lo) pivLow.push(b[i]!.low);
  }
  const ref = b[n - 1]!.close || 1;
  const tol = ref * 0.0025; // ~0.25% — pivots within this are "the same level"
  const cluster = (arr: number[], kind: "high" | "low"): DetectedLevel[] => {
    const sorted = [...arr].sort((a, c) => a - c);
    const out: DetectedLevel[] = [];
    let group: number[] = [];
    const flush = () => {
      if (group.length >= 2) out.push({ price: group.reduce((s, v) => s + v, 0) / group.length, kind, touches: group.length });
      group = [];
    };
    for (const v of sorted) { if (group.length && v - group[group.length - 1]! > tol) flush(); group.push(v); }
    flush();
    return out;
  };
  const levels = [...cluster(pivHigh, "high"), ...cluster(pivLow, "low")];
  const lo = Math.min(...b.map((c) => c.low)), hi = Math.max(...b.map((c) => c.high));
  const span = hi - lo;
  if (span > 0) {
    const inc = Math.pow(10, Math.floor(Math.log10(span))) / 2; // half-decade round increments
    if (inc > 0) for (let p = Math.ceil(lo / inc) * inc; p <= hi; p += inc) {
      if (!levels.some((l) => Math.abs(l.price - p) < tol)) levels.push({ price: p, kind: "round", touches: 1 });
    }
  }
  return levels.sort((a, c) => c.touches - a.touches).slice(0, 12);
}
